import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { Subject, take, takeUntil, finalize, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  NotificationData,
  NotificationService,
  LprEntryUpdatedNotification,
} from '../../services/notification.service';
import { ModalStateService } from '../../services/modal-state.service';
import { CancellationService } from '../../services/cancellation.service';
import { FullStoryService } from '../../services/fullstory.service';
import {
  WayPurchaseModalStateService,
  WayPurchaseModalState,
} from './services/way-purchase-modal-state.service';
import { MultiEligibleSelectionModalStateService } from '../multi-eligible-selection-modal/services/multi-eligible-selection-modal-state.service';
import { WayPurchaseApiService } from './services/way-purchase-api.service';
import {
  pickString,
  readPurchase,
  readRecord,
  isOrderRequestLookupSource,
} from './utils/way-purchase-display.helpers';
import { WayPurchaseSingleWashLayoutComponent } from './components/way-purchase-single-wash-layout/way-purchase-single-wash-layout.component';
import { WayPurchaseActiveSubscriptionLayoutComponent } from './components/way-purchase-active-subscription-layout/way-purchase-active-subscription-layout.component';
import { WayPurchaseSelectionLayoutComponent } from './components/way-purchase-selection-layout/way-purchase-selection-layout.component';
import {
  getWayPurchaseModalTitle,
  shouldShowWayPurchaseCancelButton,
  shouldShowWayPurchaseConfirmButton,
  shouldShowWayPurchaseDismissButton,
  getWayPurchaseConfirmButtonLabel,
  getWayPurchaseConfirmLoadingLabel,
  getWayPurchaseDismissButtonLabel,
  isWayPurchaseRedemptionAlreadyCompleted,
  WAY_ALREADY_REDEEMED_TODAY_ERROR_CODE,
} from './utils/way-purchase-notification.util';
import { buildConfirmWayPurchaseRequest } from './utils/way-purchase-single-wash-display.util';
import {
  buildConfirmShopPurchaseRequest,
  buildCreateOrderRequestFromUnredeemable,
  buildNotificationFromConfirmShopPurchaseResponse,
  buildOrderSuccessNotificationFromConfirmWayResponse,
  buildShopSelectionNotification,
  buildWaySelectionConfirmContext,
  isShopPurchaseOption,
  isWayPurchaseOption,
  selectionHasNoUsablePurchaseOptions,
} from './utils/way-purchase-selection-handoff.util';
import { ActiveSubscriptionGateService } from '../../services/active-subscription-gate.service';
import {
  isWayPurchasePlateInputValid,
  mergeAttendantPlateIntoWayData,
  normalizeWayPurchasePlateInput,
  resolvePlateInputForWayOption,
  WAY_PURCHASE_PLATE_REQUIRED_MESSAGE,
  wayPurchaseNeedsPlateEntry,
  wayPurchaseOptionNeedsPlateEntry,
} from './utils/way-purchase-plate.util';
import {
  isWayPurchaseUnredeemableDetailView,
  resolveWayPurchaseUnredeemableHandoffData,
  resolveWayPurchaseUnredeemableReason,
} from './utils/way-purchase-layout.util';
import { openRoutedNotificationModal } from './utils/way-purchase-modal-open.util';
import { openPusherNotificationModal } from '../../shared/utils/notification-modal-open.util';
import { normalizePrepaidRedeemNotificationForModal } from '../../shared/utils/get-lp-image-prepaid-redeem.util';
import { extractErrorCode, extractErrorMessage } from '../../shared/utils/error.util';
import {
  isRedeemableWayPurchase,
  WAY_SUBSCRIPTION_ALREADY_REDEEMED_NOTICE,
} from './utils/way-purchase-lookup-orchestration.util';
import { resolveLprEntryIdFromNotificationData } from '../../notifications/notification.utils';
import { NotificationOrderRequestWayLookupService } from '../notification-modal/services/notification-order-request-way-lookup.service';
import { GateService } from '../../services/gate.service';
import { OrdersService } from '../../orders/services/orders.service';
import {
  canOpenGateForWayPurchaseNotification,
  resolveNotificationLaneId,
  resolveNotificationLocationId,
} from '../../shared/utils/gate-lane.util';
import {
  PurchaseOption,
  WayNormalizedPurchase,
  WayShopPurchaseOption,
} from './models/way-purchase.models';

const SHOP_CANCEL_REASON = 'Attendant selected alternate purchase at gate';

interface PendingWayConfirmContext {
  lprEntryId: string | null;
  wayPassId: string | null;
  notificationType: string;
}

@Component({
  selector: 'app-way-purchase-modal',
  standalone: true,
  imports: [
    CommonModule,
    WayPurchaseSingleWashLayoutComponent,
    WayPurchaseActiveSubscriptionLayoutComponent,
    WayPurchaseSelectionLayoutComponent,
  ],
  templateUrl: './way-purchase-modal.component.html',
  styleUrls: ['./way-purchase-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20px) scale(0.95)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0) scale(1)' })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-10px) scale(0.95)' })),
      ]),
    ]),
  ],
})
export class WayPurchaseModalComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  isOpen = false;
  notification: NotificationData | null = null;
  confirmError = '';
  plateInputError = '';
  cardErrors: Record<string, string> = {};
  isConfirming = false;
  canceledOrderIds = new Set<string>();
  activeCardId: string | null = null;
  plateInput = '';
  needsPlateEntry = false;
  cardPlateInputs: Record<string, string> = {};
  /** Captured at confirm start — Pusher in-place updates can wipe `source` before close runs. */
  private closeOrderRequestAfterRedeem = false;
  /** Binds async confirm errors to the notification that initiated the request. */
  private pendingWayConfirm: PendingWayConfirmContext | null = null;

  constructor(
    private readonly wayPurchaseModalState: WayPurchaseModalStateService,
    private readonly multiEligibleModalState: MultiEligibleSelectionModalStateService,
    private readonly modalStateService: ModalStateService,
    private readonly wayPurchaseApi: WayPurchaseApiService,
    private readonly cancellationService: CancellationService,
    private readonly notificationService: NotificationService,
    private readonly orderRequestWayLookupService: NotificationOrderRequestWayLookupService,
    private readonly activeSubscriptionGateService: ActiveSubscriptionGateService,
    private readonly gateService: GateService,
    private readonly ordersService: OrdersService,
    private readonly fullStoryService: FullStoryService
  ) {}

  ngOnInit(): void {
    this.wayPurchaseModalState.modalState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: WayPurchaseModalState) => {
        this.isOpen = state.isOpen;
        this.notification = state.notification;
        if (state.isOpen) {
          this.confirmError = '';
          this.plateInputError = '';
          this.cardErrors = {};
          this.isConfirming = false;
          this.activeCardId = null;
          this.canceledOrderIds = new Set();
          this.closeOrderRequestAfterRedeem = false;
          this.syncPlateEntryState(state.notification);
        } else {
          this.plateInput = '';
          this.needsPlateEntry = false;
          this.cardPlateInputs = {};
        }
      });

    this.notificationService.lprEntryUpdatedNotification$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: LprEntryUpdatedNotification) => {
        this.applyInPlaceUpdate(event);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getTitle(): string {
    if (!this.notification) {
      return 'Way purchase';
    }
    return getWayPurchaseModalTitle(this.notification);
  }

  showConfirmButton(): boolean {
    return shouldShowWayPurchaseConfirmButton(this.notification);
  }

  showCancelButton(): boolean {
    return shouldShowWayPurchaseCancelButton(this.notification);
  }

  showDismissButton(): boolean {
    return shouldShowWayPurchaseDismissButton(this.notification);
  }

  getDismissButtonLabel(): string {
    return getWayPurchaseDismissButtonLabel(this.notification);
  }

  getConfirmButtonLabel(): string {
    return getWayPurchaseConfirmButtonLabel(this.notification);
  }

  getConfirmLoadingLabel(): string {
    return getWayPurchaseConfirmLoadingLabel(this.notification);
  }

  isConfirmDisabled(): boolean {
    return this.isConfirming;
  }

  onPlateInputChange(value: string): void {
    this.plateInput = normalizeWayPurchasePlateInput(value);
    this.clearPlateValidationErrorsIfResolved();
    this.trackFullStoryPlate(this.plateInput, 'way-purchase');
  }

  onCardPlateInputChange(event: { cardId: string; value: string }): void {
    this.cardPlateInputs = {
      ...this.cardPlateInputs,
      [event.cardId]: normalizeWayPurchasePlateInput(event.value),
    };
    this.clearCardPlateValidationError(event.cardId, this.cardPlateInputs[event.cardId] ?? '');
    this.trackFullStoryPlate(this.cardPlateInputs[event.cardId] ?? '', 'way-purchase');
  }

  private trackFullStoryPlate(plate: string, source: string): void {
    const normalized = String(plate || '').trim();
    if (normalized.length < 3) {
      return;
    }
    this.fullStoryService.trackLicensePlate({ plate: normalized, source });
  }

  private clearPlateValidationErrorsIfResolved(): void {
    if (this.plateInputError && isWayPurchasePlateInputValid(this.plateInput)) {
      this.plateInputError = '';
    }
  }

  private clearCardPlateValidationError(cardId: string, plate: string): void {
    if (
      this.cardErrors[cardId] === WAY_PURCHASE_PLATE_REQUIRED_MESSAGE &&
      isWayPurchasePlateInputValid(plate)
    ) {
      this.clearCardError(cardId);
    }
  }

  private setCardError(cardId: string, message: string): void {
    this.cardErrors = { ...this.cardErrors, [cardId]: message };
  }

  private setCardPlateValidationError(cardId: string): void {
    const next = { ...this.cardErrors };
    for (const [id, msg] of Object.entries(next)) {
      if (msg === WAY_PURCHASE_PLATE_REQUIRED_MESSAGE) {
        delete next[id];
      }
    }
    next[cardId] = WAY_PURCHASE_PLATE_REQUIRED_MESSAGE;
    this.cardErrors = next;
  }

  private clearCardError(cardId: string): void {
    if (!this.cardErrors[cardId]) {
      return;
    }
    const { [cardId]: _removed, ...rest } = this.cardErrors;
    this.cardErrors = rest;
  }

  private resolveSelectionCardId(option: PurchaseOption): string {
    if (isWayPurchaseOption(option)) {
      return `way:${option.purchase?.way_pass_id ?? ''}`;
    }
    if (isShopPurchaseOption(option)) {
      const orderId = option.order_id ? String(option.order_id) : '';
      return `shop:${orderId || option.redeem_event}`;
    }
    return '';
  }

  onCloseClick(): void {
    if (
      this.notification?.type === 'way-purchase-selection' &&
      selectionHasNoUsablePurchaseOptions(this.notification.data) &&
      this.tryOpenCreateOrderFromLaneUnredeemable()
    ) {
      return;
    }
    this.wayPurchaseModalState.hideModal();
  }

  onDismissClick(): void {
    if (this.tryOpenCreateOrderFromLaneUnredeemable()) {
      return;
    }
    this.wayPurchaseModalState.hideModal();
  }

  /**
   * LPR Approach A: dismiss unredeemable (or cancel selection with only unusable options)
   * into create-order-request. Order-request lookup keeps its underlying form instead.
   */
  private tryOpenCreateOrderFromLaneUnredeemable(): boolean {
    if (isOrderRequestLookupSource(this.notification?.data as Record<string, any> | undefined)) {
      this.wayPurchaseModalState.hideModal();
      return true;
    }

    const createOrder = buildCreateOrderRequestFromUnredeemable(
      resolveWayPurchaseUnredeemableHandoffData(this.notification)
    );
    if (!createOrder) {
      return false;
    }

    const row = readRecord(createOrder.data) ?? {};
    const lprEntryId = pickString(row['lpr_entry_id'], row['lprEntryId']);
    const promoteFields: Record<string, any> = {};
    const customerId = pickString(row['customer_id'], row['customerId']);
    const vehicleId = pickString(row['vehicle_id'], row['vehicleId']);
    const plate = pickString(row['plate'], row['plateNumber'], row['vehicle_license_plate']);
    const state = pickString(row['state']);
    if (customerId) {
      promoteFields['customer_id'] = customerId;
    }
    if (vehicleId) {
      promoteFields['vehicle_id'] = vehicleId;
    }
    if (plate) {
      promoteFields['plate'] = plate;
    }
    if (state) {
      promoteFields['state'] = state;
    }

    this.wayPurchaseModalState.hideModal();

    const openCreateOrder = (): void => {
      openRoutedNotificationModal(
        this.modalStateService,
        this.wayPurchaseModalState,
        {
          notification: createOrder,
          behavior: 'replace',
          shouldStore: true,
          clearDismissSuppression: true,
        },
        this.activeSubscriptionGateService,
        undefined,
        this.multiEligibleModalState
      );
    };

    if (!lprEntryId) {
      openCreateOrder();
      return true;
    }

    this.wayPurchaseApi
      .promoteLprEntryToCreateOrderRequest(lprEntryId, promoteFields)
      .pipe(
        take(1),
        catchError(() => of(null))
      )
      .subscribe(() => openCreateOrder());

    return true;
  }

  onConfirmClick(): void {
    if (!this.notification || this.isConfirming) {
      return;
    }

    if (isWayPurchaseRedemptionAlreadyCompleted(this.notification)) {
      if (canOpenGateForWayPurchaseNotification(this.notification)) {
        this.openGateForCompletedWayRedemption();
        return;
      }
      this.closeOrderRequestAfterRedeem = this.shouldCloseOrderRequestAfterRedeem();
      this.closeAfterSuccessfulWayRedeem();
      return;
    }

    if (this.needsPlateEntry && !isWayPurchasePlateInputValid(this.plateInput)) {
      this.plateInputError = WAY_PURCHASE_PLATE_REQUIRED_MESSAGE;
      return;
    }

    const confirmData = this.resolveConfirmData(this.notification.data);
    const payload = buildConfirmWayPurchaseRequest(confirmData);
    if (!payload) {
      this.confirmError = 'Missing purchase data. Cannot confirm this Way order.';
      return;
    }

    this.confirmError = '';
    this.plateInputError = '';
    this.isConfirming = true;
    this.closeOrderRequestAfterRedeem = this.shouldCloseOrderRequestAfterRedeem();
    this.beginWayConfirmContext(payload.way_pass_id);

    this.wayPurchaseApi
      .confirmWayPurchase(payload)
      .pipe(
        take(1),
        finalize(() => {
          this.isConfirming = false;
          this.clearWayConfirmContext();
        })
      )
      .subscribe({
        next: response => {
          const successNotification = this.closeOrderRequestAfterRedeem
            ? buildOrderSuccessNotificationFromConfirmWayResponse(response)
            : null;
          this.closeAfterSuccessfulWayRedeem(successNotification);
        },
        error: (error: any) => {
          this.closeOrderRequestAfterRedeem = false;
          if (this.handleAlreadyRedeemedTodayError(error)) {
            return;
          }
          this.confirmError = extractErrorMessage(
            error,
            'Failed to confirm Way purchase. Please try again.'
          );
        },
      });
  }

  onUseSelectionOption(option: PurchaseOption): void {
    if (!this.notification || this.isConfirming) {
      return;
    }

    const cardId = this.resolveSelectionCardId(option);

    if (isWayPurchaseOption(option)) {
      const data = this.notification.data as Record<string, any> | undefined;
      if (wayPurchaseOptionNeedsPlateEntry(data, option)) {
        const plate = resolvePlateInputForWayOption(data, option, this.cardPlateInputs, cardId);
        if (!isWayPurchasePlateInputValid(plate)) {
          this.setCardPlateValidationError(cardId);
          return;
        }
      }
    }

    this.clearCardError(cardId);
    this.confirmError = '';
    this.plateInputError = '';

    if (isWayPurchaseOption(option)) {
      this.confirmWaySelectionOption(option);
      return;
    }

    if (isShopPurchaseOption(option)) {
      if (
        option.redeem_event === 'order-success' ||
        option.redeem_event === 'active-subscription'
      ) {
        this.confirmShopSelectionOption(option);
        return;
      }
      this.openShopSelectionOption(option);
    }
  }

  onCancelShopSelectionOption(option: WayShopPurchaseOption): void {
    if (this.isConfirming) {
      return;
    }

    const orderId = option.order_id ? String(option.order_id) : '';
    const cardId = this.resolveSelectionCardId(option);
    if (!orderId) {
      this.setCardError(cardId, 'Missing order id. Cannot cancel this purchase.');
      return;
    }

    // TODO: Implement confirmation dialog with Angular Material Dialog
    // this.confirmationService.confirm({
    //   message: 'Cancel this in-shop order?',
    //   header: 'Confirm Cancel',
    //   accept: () => this.executeCancelShopSelection(option, orderId, cardId),
    // });

    // For now, directly execute the cancellation
    this.executeCancelShopSelection(option, orderId, cardId);
  }

  private executeCancelShopSelection(
    option: WayShopPurchaseOption,
    orderId: string,
    cardId: string
  ): void {
    this.clearCardError(cardId);
    this.confirmError = '';
    this.plateInputError = '';
    this.isConfirming = true;
    this.activeCardId = cardId;

    this.cancellationService
      .cancelOrder(orderId, SHOP_CANCEL_REASON)
      .pipe(
        take(1),
        finalize(() => {
          this.isConfirming = false;
          this.activeCardId = null;
        })
      )
      .subscribe({
        next: () => {
          this.canceledOrderIds = new Set([...this.canceledOrderIds, orderId]);
        },
        error: (error: any) => {
          this.cardErrors = {
            ...this.cardErrors,
            [cardId]: extractErrorMessage(error, 'Failed to cancel this order. Please try again.'),
          };
        },
      });
  }

  private syncPlateEntryState(notification: NotificationData | null): void {
    const data = (notification?.data ?? null) as Record<string, any> | null;
    this.needsPlateEntry =
      !isWayPurchaseUnredeemableDetailView(notification) && wayPurchaseNeedsPlateEntry(data);
    this.plateInput = '';
    this.cardPlateInputs = {};
  }

  private resolveConfirmData(
    data: NotificationData['data'] | Record<string, any> | null | undefined
  ): Record<string, any> | null {
    const row = (data ?? null) as Record<string, any> | null;
    if (!this.needsPlateEntry) {
      return row;
    }
    return mergeAttendantPlateIntoWayData(row, this.plateInput);
  }

  private resolveConfirmDataForSelectionOption(option: PurchaseOption): Record<string, any> | null {
    const row = (this.notification?.data ?? null) as Record<string, any> | null;
    if (!isWayPurchaseOption(option)) {
      return row;
    }
    const cardId = `way:${option.purchase?.way_pass_id ?? ''}`;
    const plate = resolvePlateInputForWayOption(row, option, this.cardPlateInputs, cardId);
    if (!wayPurchaseOptionNeedsPlateEntry(row, option)) {
      return row;
    }
    return mergeAttendantPlateIntoWayData(row, plate);
  }

  private confirmWaySelectionOption(option: PurchaseOption): void {
    if (!isWayPurchaseOption(option)) {
      return;
    }

    const confirmContext = buildWaySelectionConfirmContext(
      this.resolveConfirmDataForSelectionOption(option),
      option
    );
    const purchaseForConfirm =
      (confirmContext?.['purchase'] as typeof option.purchase | undefined) ?? option.purchase;
    const payload = buildConfirmWayPurchaseRequest(confirmContext, purchaseForConfirm);
    const cardId = `way:${option.purchase?.way_pass_id ?? ''}`;

    if (!payload) {
      this.setCardError(cardId, 'Missing purchase data. Cannot confirm this Way order.');
      return;
    }

    this.clearCardError(cardId);

    this.isConfirming = true;
    this.activeCardId = cardId;
    this.closeOrderRequestAfterRedeem = this.shouldCloseOrderRequestAfterRedeem();
    this.beginWayConfirmContext(option.purchase?.way_pass_id ?? null);

    this.wayPurchaseApi
      .confirmWayPurchase(payload)
      .pipe(
        take(1),
        finalize(() => {
          this.isConfirming = false;
          this.activeCardId = null;
          this.clearWayConfirmContext();
        })
      )
      .subscribe({
        next: response => {
          const successNotification = this.closeOrderRequestAfterRedeem
            ? buildOrderSuccessNotificationFromConfirmWayResponse(response)
            : null;
          this.closeAfterSuccessfulWayRedeem(successNotification);
        },
        error: (error: any) => {
          this.closeOrderRequestAfterRedeem = false;
          if (this.handleAlreadyRedeemedTodayError(error, option.purchase)) {
            return;
          }
          this.setCardError(
            cardId,
            extractErrorMessage(error, 'Failed to confirm Way purchase. Please try again.')
          );
        },
      });
  }

  /** True when Way overlay was opened from order-request plate/phone/email lookup. */
  private shouldCloseOrderRequestAfterRedeem(): boolean {
    if (isOrderRequestLookupSource(this.notification?.data as Record<string, any> | undefined)) {
      return true;
    }
    // Banner means lookup found a purchase while order-request was open underneath.
    return !!(this.orderRequestWayLookupService.showWayPurchaseReopenBanner$ as any)?.value;
  }

  /** Manual gate after server-side Way auto-redeem: open gate, then dismiss like a successful redeem. */
  private openGateForCompletedWayRedemption(): void {
    const data = this.notification?.data as Record<string, any> | undefined;
    const locationId = resolveNotificationLocationId(data);
    const laneId = resolveNotificationLaneId(data);

    if (!canOpenGateForWayPurchaseNotification(this.notification)) {
      this.confirmError = 'Gate is not configured for this lane.';
      return;
    }

    this.confirmError = '';
    this.isConfirming = true;
    this.closeOrderRequestAfterRedeem = this.shouldCloseOrderRequestAfterRedeem();

    this.gateService
      .openLaneGate(locationId, laneId)
      .pipe(
        take(1),
        finalize(() => {
          this.isConfirming = false;
        })
      )
      .subscribe({
        next: () => {
          this.ordersService.refreshOrders();
          this.closeAfterSuccessfulWayRedeem();
        },
        error: (error: any) => {
          this.closeOrderRequestAfterRedeem = false;
          this.confirmError = extractErrorMessage(error, 'Failed to open gate. Please try again.');
        },
      });
  }

  /**
   * Close Way modal after redeem. When opened from order-request manual lookup,
   * also close the underlying order-request modal (it stays open during lookup overlay).
   */
  private closeAfterSuccessfulWayRedeem(successNotification?: NotificationData | null): void {
    const closeOrderRequest = this.closeOrderRequestAfterRedeem;
    this.closeOrderRequestAfterRedeem = false;
    this.wayPurchaseModalState.hideModal();
    if (!closeOrderRequest) {
      return;
    }
    this.orderRequestWayLookupService.clearStoredWayPurchase();
    // Drop anything queued during hide (e.g. order-success from confirm) so order-request
    // does not reopen after the close animation.
    this.modalStateService.clearNotificationQueue();
    if (this.modalStateService.getCurrentState().isOpen) {
      this.modalStateService.hideModal();
    }
    if (successNotification) {
      setTimeout(() => {
        this.modalStateService.clearNotificationQueue();
        openPusherNotificationModal(
          this.modalStateService,
          this.wayPurchaseModalState,
          successNotification,
          undefined,
          undefined,
          this.multiEligibleModalState
        );
      }, 400);
    } else {
      setTimeout(() => this.modalStateService.clearNotificationQueue(), 400);
    }
  }

  private confirmShopSelectionOption(option: WayShopPurchaseOption): void {
    const payload = buildConfirmShopPurchaseRequest(this.notification?.data, option);
    const cardId = this.resolveSelectionCardId(option);

    if (!payload) {
      this.setCardError(cardId, 'Missing shop purchase data. Cannot confirm this order.');
      return;
    }

    this.clearCardError(cardId);

    this.isConfirming = true;
    this.activeCardId = cardId;

    this.wayPurchaseApi
      .confirmShopPurchase(payload)
      .pipe(
        take(1),
        finalize(() => {
          this.isConfirming = false;
          this.activeCardId = null;
        })
      )
      .subscribe({
        next: response => {
          this.wayPurchaseModalState.hideModal();
          const notification = buildNotificationFromConfirmShopPurchaseResponse(
            response,
            payload.redeem_event
          );
          if (notification) {
            openPusherNotificationModal(
              this.modalStateService,
              this.wayPurchaseModalState,
              notification,
              this.activeSubscriptionGateService,
              undefined,
              this.multiEligibleModalState
            );
          }
        },
        error: (error: any) => {
          this.setCardError(
            cardId,
            extractErrorMessage(error, 'Failed to confirm shop purchase. Please try again.')
          );
        },
      });
  }

  private openShopSelectionOption(option: WayShopPurchaseOption): void {
    const shopNotification = buildShopSelectionNotification(
      this.resolveConfirmData(this.notification?.data),
      option
    );
    if (!shopNotification) {
      const cardId = this.resolveSelectionCardId(option);
      this.setCardError(cardId, 'Missing shop purchase data. Cannot open redemption flow.');
      return;
    }

    const normalized = normalizePrepaidRedeemNotificationForModal(shopNotification);
    this.wayPurchaseModalState.hideModal();
    openRoutedNotificationModal(
      this.modalStateService,
      this.wayPurchaseModalState,
      {
        notification: normalized,
        behavior: 'respect_state',
        shouldStore: true,
      },
      undefined,
      undefined,
      this.multiEligibleModalState
    );
  }

  private applyInPlaceUpdate(event: LprEntryUpdatedNotification): void {
    if (!this.isOpen || !this.notification) {
      return;
    }

    const currentEntryId = resolveLprEntryIdFromNotificationData(
      this.notification.data as Record<string, any> | undefined
    );
    const updatedEntryId = resolveLprEntryIdFromNotificationData(
      event.notification.data as Record<string, any> | undefined
    );

    if (!currentEntryId || currentEntryId !== updatedEntryId) {
      return;
    }

    const previousData = (this.notification.data ?? {}) as Record<string, any>;
    const nextData = (event.notification.data ?? {}) as Record<string, any>;
    this.notification = this.coerceWayPurchaseNotificationForPurchaseState({
      ...event.notification,
      timestamp: this.notification.timestamp,
      data: {
        ...nextData,
        // Keep order-request lookup provenance across LPR in-place updates.
        ...(previousData['source'] != null && nextData['source'] == null
          ? { source: previousData['source'] }
          : {}),
      },
    });
    this.confirmError = '';
    this.needsPlateEntry =
      !isWayPurchaseUnredeemableDetailView(this.notification) &&
      wayPurchaseNeedsPlateEntry(this.notification.data as Record<string, any> | undefined);
  }

  /**
   * Way confirm can return already-redeemed for stale membership rows. Switch to the
   * dedicated already-redeemed dialog (member details + Continue to new order).
   */
  private handleAlreadyRedeemedTodayError(
    error: any,
    purchaseOverride?: WayNormalizedPurchase | null
  ): boolean {
    if (extractErrorCode(error) !== WAY_ALREADY_REDEEMED_TODAY_ERROR_CODE) {
      return false;
    }
    if (!this.isWayConfirmContextCurrent()) {
      return false;
    }
    const apiMessage = extractErrorMessage(error, WAY_SUBSCRIPTION_ALREADY_REDEEMED_NOTICE);
    this.morphToAlreadyRedeemedTodayDialog(apiMessage, purchaseOverride);
    return true;
  }

  /** Show member details with already-redeemed notice and hand off to a new order. */
  private morphToAlreadyRedeemedTodayDialog(
    reason: string,
    purchaseOverride?: WayNormalizedPurchase | null
  ): void {
    if (!this.notification) {
      return;
    }

    const row = readRecord(this.notification.data) ?? {};
    const purchase = purchaseOverride ?? readPurchase(row);
    const resolvedReason = reason.trim() || WAY_SUBSCRIPTION_ALREADY_REDEEMED_NOTICE;
    const updatedPurchase = purchase
      ? {
          ...purchase,
          can_redeem: false,
          redeemed_today: true,
          unredeemable_reason: resolvedReason,
        }
      : null;

    this.notification = {
      ...this.notification,
      type: 'way-subscription-already-redeemed-today',
      message: resolvedReason,
      data: {
        ...row,
        ...(updatedPurchase ? { purchase: updatedPurchase } : {}),
        reason: resolvedReason,
      },
    };
    this.confirmError = '';
    this.plateInputError = '';
    this.cardErrors = {};
    this.needsPlateEntry = false;
  }

  private beginWayConfirmContext(wayPassId?: string | null): void {
    const row = readRecord(this.notification?.data) ?? {};
    this.pendingWayConfirm = {
      lprEntryId: resolveLprEntryIdFromNotificationData(row),
      wayPassId: wayPassId ?? readPurchase(row)?.way_pass_id ?? null,
      notificationType: this.notification?.type ?? '',
    };
  }

  private clearWayConfirmContext(): void {
    this.pendingWayConfirm = null;
  }

  private isWayConfirmContextCurrent(): boolean {
    const pending = this.pendingWayConfirm;
    if (!pending || !this.notification) {
      return false;
    }

    const row = readRecord(this.notification.data) ?? {};
    const currentEntryId = resolveLprEntryIdFromNotificationData(row);
    if (pending.lprEntryId != null && currentEntryId !== pending.lprEntryId) {
      return false;
    }

    if (pending.notificationType !== this.notification.type) {
      return false;
    }

    if (pending.wayPassId) {
      const currentPurchase = readPurchase(row);
      if (
        currentPurchase?.way_pass_id &&
        String(currentPurchase.way_pass_id) !== String(pending.wayPassId)
      ) {
        return false;
      }
      if (
        !currentPurchase &&
        pending.notificationType === 'way-purchase-selection' &&
        this.activeCardId !== `way:${pending.wayPassId}`
      ) {
        return false;
      }
    }

    return true;
  }

  /** Align notification type/reason when purchase becomes unredeemable after LPR refresh. */
  private coerceWayPurchaseNotificationForPurchaseState(
    notification: NotificationData
  ): NotificationData {
    if (
      notification.type !== 'way-active-subscription' &&
      notification.type !== 'way-single-wash'
    ) {
      return notification;
    }

    const row = readRecord(notification.data);
    const purchase = readPurchase(row);
    if (!purchase || isRedeemableWayPurchase(purchase)) {
      return notification;
    }

    const purchaseType = String(purchase.purchase_type ?? '')
      .trim()
      .toLowerCase();
    const isAlreadyRedeemedToday =
      purchaseType === 'membership' &&
      purchase.can_redeem === false &&
      purchase.redeemed_today === true;
    const reason = resolveWayPurchaseUnredeemableReason(row, notification.message);

    return {
      ...notification,
      type: isAlreadyRedeemedToday
        ? 'way-subscription-already-redeemed-today'
        : 'way-purchase-unredeemable',
      message: reason,
      data: {
        ...(row ?? {}),
        purchase,
        reason,
      },
    };
  }
}
